import { createContext, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AgentSocket, AssistantState } from '@/api/realtime';
import { aiKey } from '@/api/ai';
import { ticketsKey } from '@/api/tickets';
import type { Message } from '@/api/types';

/** The workspace's live socket; null outside a workspace or before it opens. */
export const RealtimeContext = createContext<AgentSocket | null>(null);

/**
 * Keeps one open ticket live: joins its room (again after every reconnect), appends pushed messages
 * to the cached thread and refetches the ticket header once when it changes — no polling.
 * Returns whether the AI assistant is preparing a reply on it right now.
 */
export function useLiveTicket(ticketId: string | undefined): {
  assistantTyping: boolean;
  /** The AI agent's name while it is typing. */
  assistantName: string | null;
} {
  const socket = useContext(RealtimeContext);
  const qc = useQueryClient();
  const [typing, setTyping] = useState<{ ticketId: string; on: boolean; name: string | null } | null>(null);

  useEffect(() => {
    if (!socket || !ticketId) return;
    const detailKey = [...ticketsKey, 'detail', ticketId];
    const join = () => socket.emit('ticket:join', { ticketId });
    const refreshDetail = () => void qc.invalidateQueries({ queryKey: detailKey, exact: true });

    const onMessage = (event: { ticketId: string; message: Message }) => {
      if (event.ticketId !== ticketId) return;
      qc.setQueryData<Message[]>([...detailKey, 'messages'], (prev) =>
        prev && !prev.some((m) => m.id === event.message.id) ? [...prev, event.message] : prev,
      );
      // A new message can move status / last activity without a separate ticket event,
      // and an AI reply adds to the ticket's token usage.
      refreshDetail();
      void qc.invalidateQueries({ queryKey: [...aiKey, 'ticket-usage', ticketId] });
    };
    const onUpdated = (event: { ticketId: string }) => {
      if (event.ticketId === ticketId) refreshDetail();
    };
    const onAssistant = (event: { ticketId: string; state: AssistantState; agentName: string | null }) => {
      if (event.ticketId === ticketId)
        setTyping({ ticketId, on: event.state === 'thinking', name: event.agentName ?? null });
    };

    if (socket.connected) join();
    socket.on('connect', join);
    socket.on('message:new', onMessage);
    socket.on('ticket:updated', onUpdated);
    socket.on('assistant:status', onAssistant);
    return () => {
      socket.off('connect', join);
      socket.off('message:new', onMessage);
      socket.off('ticket:updated', onUpdated);
      socket.off('assistant:status', onAssistant);
      if (socket.connected) socket.emit('ticket:leave', { ticketId });
    };
  }, [socket, ticketId, qc]);

  // Scoped to the ticket, so switching chats never carries a stale indicator over.
  const live = !!typing?.on && typing.ticketId === ticketId;
  return { assistantTyping: live, assistantName: live ? (typing?.name ?? null) : null };
}
